import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Radio, X } from "lucide-react";
import { RealtimeVoice, detectAssociate } from "@/lib/realtime";

// A silent, always-listening voice "front desk". It never speaks — it just
// transcribes what you say and, the moment you name an associate, hands you
// straight over to them. Rendered only while no call is open so there is
// never more than one microphone session live at a time.
// Pull out the user's actual question if they said more than just a name/greeting
// (e.g. "Hi Miles, how's your day?" → keep the whole utterance as context).
function extractQuery(text, name) {
  const original = (text || "").trim();
  let rest = original.toLowerCase();
  if (name) rest = rest.replace(new RegExp(`\\b${name.toLowerCase()}\\b`, "g"), " ");
  rest = rest.replace(/\b(hi|hey|hello|yo|talk to|speak (?:to|with)|let me talk to|switch to|connect(?: me)? to|can i talk to|i want to talk to|please|and)\b/g, " ");
  rest = rest.replace(/[^\w?]+/g, " ").trim();
  return rest.length >= 3 ? original : null;
}

export function FrontDesk({ employees, onRoute, onStop }) {
  const [status, setStatus] = useState("connecting");
  const [heard, setHeard] = useState("");
  const rv = useRef(null);
  const empRef = useRef(employees);
  const routeRef = useRef(onRoute);
  useEffect(() => { empRef.current = employees; }, [employees]);
  useEffect(() => { routeRef.current = onRoute; }, [onRoute]);

  useEffect(() => {
    const v = new RealtimeVoice((s) => {
      if (s.status) setStatus(s.status);
      if (s.userTranscript) {
        setHeard(s.userTranscript);
        const id = detectAssociate(s.userTranscript, empRef.current);
        if (id) {
          const emp = (empRef.current || []).find((e) => e.id === id);
          const query = extractQuery(s.userTranscript, emp?.name || "");
          routeRef.current && routeRef.current(id, query);
        }
      }
    });
    rv.current = v;
    v.start(
      "You are a silent switchboard operator for a business called ABC Services. Do NOT speak, greet, or produce any audio under any circumstances. Simply listen.",
      "alloy",
      { greet: false, autoRespond: false, transcribe: true }
    ).catch(() => setStatus("error"));
    return () => { v.stop(); rv.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connecting = status === "connecting";
  const error = status === "error";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2"
      data-testid="front-desk-listening"
    >
      <div className="glass-strong flex items-center gap-3 rounded-full border border-[#00E5FF]/25 px-4 py-2.5 shadow-lg">
        <span className="relative grid h-8 w-8 place-items-center rounded-full" style={{ background: error ? "#FF005522" : "#00E5FF1A" }}>
          {connecting ? (
            <Loader2 size={16} className="animate-spin text-[#00E5FF]" />
          ) : (
            <>
              {!error && [0, 1].map((i) => (
                <motion.span key={i} className="absolute rounded-full" style={{ width: 26, height: 26, border: "1px solid #00E5FF" }}
                  initial={{ opacity: 0.5, scale: 1 }} animate={{ opacity: 0, scale: 1.6 }} transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.6 }} />
              ))}
              <Radio size={16} className={error ? "text-[#FF3B5C]" : "text-[#00E5FF]"} />
            </>
          )}
        </span>
        <div className="min-w-0">
          <div className="text-xs font-semibold text-white">
            {error ? "Mic unavailable" : connecting ? "Front desk connecting…" : "Front desk · listening"}
          </div>
          <div className="max-w-[240px] truncate text-[11px] text-slate-400">
            {error ? "Allow microphone access to use voice" : heard ? `“${heard}”` : "Say an associate's name to connect"}
          </div>
        </div>
        <button
          onClick={onStop}
          className="ml-1 grid h-7 w-7 place-items-center rounded-full text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Turn off front desk"
          data-testid="front-desk-stop"
        >
          <X size={15} />
        </button>
      </div>
    </motion.div>
  );
}
